// Location: src/features/devices/hooks/useDevicePermissions.ts

import { hasResourcePermission } from '@/config/resourceConfig';
import { usePermissions } from 'react-admin';
import type { UserRole } from '@/types/models';

/**
 * Hook to get device permissions for current user
 *
 * Permissions by role:
 * - SuperAdmin: Full access (list, create, edit, delete, forceDelete, export, generateOtp)
 * - GroupAdmin: List, show, generateOtp (read-only for devices in their groups)
 * - HotelAdmin: List, show, generateOtp (read-only for devices in their hotels)
 * - Receptionist: generateOtp only (Llegadas «Código de operador»)
 * - Viewer: no operator code (product decision P06; the API refuses it too)
 */
/** P06 (refined 15/10): the dispenser operator code — Receptionist, Hotel administrator or above; never Viewer. */
export const canGenerateOperatorCode = (role: UserRole | undefined | null): boolean =>
  role === 'SuperAdmin' || role === 'Admin' || role === 'GroupAdmin' || role === 'HotelAdmin' || role === 'Receptionist';

export const useDevicePermissions = () => {
  const { permissions } = usePermissions();
  const role = permissions as UserRole;
  const isSuperAdmin = role === 'SuperAdmin' || role === 'Admin';

  const canList = hasResourcePermission('devices', 'list');
  const canCreate = hasResourcePermission('devices', 'create');
  const canEdit = hasResourcePermission('devices', 'edit');
  const canDelete = hasResourcePermission('devices', 'delete');
  const canShow = hasResourcePermission('devices', 'show');
  const canExport = hasResourcePermission('devices', 'export');
  const canForceDelete = isSuperAdmin; // Only SuperAdmin can force delete
  const canGenerateOtp = canGenerateOperatorCode(role);

  return {
    canList,
    canCreate,
    canEdit,
    canDelete,
    canShow,
    canExport,
    canForceDelete,
    canGenerateOtp,
  };
};